import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { useRealtime } from '../../hooks/useRealtime';
import { playChime, setupAudioUnlock } from '../../lib/audio';
import { formatUsdSigned } from '../../lib/format';
import { tg } from '../../lib/tg';
import type { RealtimeEnvelope } from '../../lib/realtime';

interface ResolvedPayload {
  position_id?: string;
  realized_pnl_usdc?: string;
  win?: boolean;
}

interface Snapshot {
  id: number;
  pnl: number;
  win: boolean;
}

const DURATION_MS = 3200;

/**
 * Mounted near the app root. Listens for `user:*:trade` events of
 * type=settled / resolved and pops a confetti + animated PnL overlay.
 *
 * Sound — see lib/audio.ts. Mute toggle: `polybot.prefs.sound`.
 *
 * Reduced motion:
 *   - confetti skipped when prefers-reduced-motion is set; we still
 *     fire the haptic so confirmation still happens.
 */
export function TradeResolvedOverlay() {
  const { t } = useTranslation();
  const [snap, setSnap] = useState<Snapshot | null>(null);

  useEffect(() => {
    setupAudioUnlock();
  }, []);

  useRealtime(
    (ch) => ch.endsWith(':trade'),
    (env: RealtimeEnvelope) => {
      if (env.type !== 'settled' && env.type !== 'resolved') return;
      const p = env.payload as ResolvedPayload;
      const pnl = Number(p.realized_pnl_usdc ?? 0);
      const win = p.win ?? pnl > 0;
      const id = Date.now();
      setSnap({ id, pnl, win });

      if (win) {
        tg.hapticSuccess();
      } else if (pnl < 0) {
        tg.hapticError();
      }
      playChime(win);
    },
  );

  useEffect(() => {
    if (!snap) return;
    const id = window.setTimeout(() => setSnap(null), DURATION_MS);
    return () => window.clearTimeout(id);
  }, [snap]);

  if (!snap) return null;

  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center px-6"
      role="status"
      aria-live="polite"
      data-testid="trade-resolved-overlay"
    >
      <div className="absolute inset-0 bg-tg-bg/40 backdrop-blur-[2px] pb-scrim" />
      <div className="relative w-full max-w-app rounded-pb-xl border border-pb-border bg-pb-paper px-6 py-8 text-center shadow-pb-lg pb-fade-up">
        <p
          className={clsx(
            'pb-eyebrow !text-[12px] !tracking-[0.12em]',
            snap.win ? 'text-pb-success' : 'text-pb-danger',
          )}
        >
          {snap.win ? t('overlay.win_eyebrow') : t('overlay.loss_eyebrow')}
        </p>
        <p
          className={clsx(
            'pb-numeral-lg mt-3 text-[40px]',
            snap.win ? 'text-pb-success' : 'text-pb-danger',
          )}
        >
          {formatUsdSigned(snap.pnl)}
        </p>
        <p className="pb-caption mt-3">
          {snap.win ? t('overlay.win_body') : t('overlay.loss_body')}
        </p>
      </div>
      {!reduced && snap.win ? <Confetti /> : null}
    </div>
  );
}

function Confetti() {
  // Cheap, dependency-free confetti — 24 absolutely-positioned divs
  // with randomized translate / rotate / hue, animated by CSS keyframe.
  const flakes = Array.from({ length: 24 }, (_, i) => i);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {flakes.map((i) => {
        const left = (i * 4.2) % 100;
        const delay = (i % 8) * 60;
        const hue = (i * 47) % 360;
        return (
          <span
            key={i}
            className="pb-confetti absolute top-[-10px] h-2 w-2 rounded-[1px]"
            style={{
              left: `${left}%`,
              animationDelay: `${delay}ms`,
              backgroundColor: `hsl(${hue}deg 80% 60%)`,
            }}
          />
        );
      })}
    </div>
  );
}

